/* Adivaa Power — front-end behaviour for the ap-* sections.
   No dependencies, no external requests. Every feature degrades to a usable
   static state if JS never runs. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  /* Live, not a snapshot: a tablet with a keyboard folio flips between the two
     mid-session, so every caller asks at event time rather than at load. */
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hoverable = function () { return finePointer.matches; };
  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  /* =======================================================================
     MOTION — the shared half of the animation system. The rules these drive
     live in the MOTION block of build/tailwind.css. Two ideas only: things
     SETTLE into place once, and the accent travels as CURRENT along an axis.
     ======================================================================= */

  var IO = 'IntersectionObserver' in window;

  /* Replay a CSS animation. Dropping the class is not enough on its own — the
     browser only queues a fresh run once the node has been reflowed. */
  var fire = function (el, cls) {
    if (!el || reduced) return;
    var c = cls || 'is-firing';
    el.classList.remove(c);
    void el.offsetWidth;
    el.classList.add(c);
  };

  /* Run fn the first time el is on screen, then stop watching it. */
  var once = function (el, fn, ratio) {
    if (!el) return;
    if (!IO) { fn(el); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.disconnect();
        fn(el);
      });
    }, { threshold: ratio == null ? 0.12 : ratio, rootMargin: '0px 0px -6% 0px' });
    io.observe(el);
  };

  /* Wrap every word of a heading in its own mask so the line can rise in.
     Only text nodes are touched, so <b>/<i> emphasis — and the accent colour
     it carries — survives the split. Returns the word count. */
  var splitWords = function (el) {
    if (!el || el.getAttribute('data-ap-split') === 'done') return 0;
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [], node;
    while ((node = walker.nextNode())) nodes.push(node);
    var i = 0;
    nodes.forEach(function (t) {
      var parts = t.nodeValue.split(/(\s+)/);
      if (parts.length < 1) return;
      var frag = document.createDocumentFragment();
      parts.forEach(function (p) {
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
        var outer = document.createElement('span');
        outer.className = 'ap-w';
        var inner = document.createElement('span');
        inner.className = 'ap-w-i';
        inner.style.setProperty('--i', i++);
        inner.textContent = p;
        outer.appendChild(inner);
        frag.appendChild(outer);
      });
      if (t.parentNode) t.parentNode.replaceChild(frag, t);
    });
    el.setAttribute('data-ap-split', 'done');
    el.classList.add('is-ready');
    return i;
  };

  /* --------------------------------------------------------------- marquee */
  /* The announcement bar must never show a gap or the same message twice at
     once. A single group is measured first: if it is narrower than the screen
     the item gaps are stretched until it spans, so a full set always fills the
     bar. Groups are then cloned until the track covers the screen plus one
     group, and the keyframe shifts by exactly one measured group width — so
     the loop is seamless at any width and any number of messages. */
  function marquee() {
    var track = document.querySelector('[data-ap-marquee-track]');
    if (!track) return;
    var wrap = track.parentNode;
    var first = track.querySelector('.ap-marquee-group');
    if (!first) return;

    var layout = function () {
      /* back to one group with unstretched gaps before measuring */
      track.style.setProperty('--ap-marquee-extra', '0px');
      while (track.children.length > 1) track.removeChild(track.lastElementChild);

      var wrapW = wrap.getBoundingClientRect().width;
      var items = first.querySelectorAll('.ap-marquee-item').length;
      if (!items || !wrapW) return;

      var groupW = first.getBoundingClientRect().width;
      if (groupW < wrapW) {
        track.style.setProperty('--ap-marquee-extra', ((wrapW - groupW) / items) + 'px');
        groupW = first.getBoundingClientRect().width;
      }

      for (var i = 0; i < 12 && track.getBoundingClientRect().width < wrapW + groupW + 1; i++) {
        var clone = first.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (n) {
          n.removeAttribute('data-shopify-editor-block');
        });
        clone.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
        track.appendChild(clone);
      }
      track.style.setProperty('--ap-marquee-w', groupW + 'px');
    };

    layout();
    window.addEventListener('resize', layout, { passive: true });
    window.addEventListener('load', layout);
    /* webfonts land after first paint and change the measured width */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout).catch(function () {});
  }

  /* ------------------------------------------------------------------- faq */
  /* Questions can be grouped (tracks vs sockets); the tabs swap which group is
     on show. Every question stays in the DOM so the FAQPage markup beside the
     accordion keeps matching what the page contains. */
  function faqTabs() {
    document.querySelectorAll('[data-ap-faq-tabs]').forEach(function (row) {
      var section = row.closest('section') || document;
      var tabs = Array.prototype.slice.call(row.querySelectorAll('[data-ap-faq-tab]'));
      var items = Array.prototype.slice.call(section.querySelectorAll('[data-ap-faq-item]'));
      if (tabs.length < 2) return;

      var select = function (name) {
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-group') === name;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', String(on));
        });
        items.forEach(function (it) {
          var g = it.getAttribute('data-group');
          var on = !g || g === name;
          it.hidden = !on;
          if (!on) it.removeAttribute('open');   // a hidden answer should not stay expanded
        });
      };

      tabs.forEach(function (t) {
        t.addEventListener('click', function () { select(t.getAttribute('data-group')); });
      });
      select(tabs[0].getAttribute('data-group'));
    });
  }

  /* ---------------------------------------------------------------- header */
  /* The header group is sticky with a negative top equal to the marquee's
     height, so the marquee scrolls away and the nav row stays. Both heights
     are measured here into CSS variables; the section tabs pin under them. */
  function header() {
    var el = document.querySelector('[data-ap-header]');
    var ann = document.querySelector('[data-ap-ann]');
    var root = document.documentElement;

    var measure = function () {
      root.style.setProperty('--ap-ann-h', (ann ? ann.offsetHeight : 0) + 'px');
      if (el) root.style.setProperty('--ap-header-h', el.offsetHeight + 'px');
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure);
    if (!el) return;

    /* the header only earns its shadow once there is page above it */
    var shade = function () { el.classList.toggle('is-scrolled', window.scrollY > 8); };
    window.addEventListener('scroll', shade, { passive: true });
    shade();

    /* drawer */
    var toggle = el.querySelector('[data-ap-menu-toggle]');
    var drawer = el.querySelector('[data-ap-mobile-menu]');
    if (!toggle || !drawer) return;
    var setOpen = function (open) {
      el.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        var first = drawer.querySelector('[data-ap-menu-close]');
        if (first) first.focus();
      } else toggle.focus();
    };
    toggle.addEventListener('click', function () { setOpen(!el.classList.contains('menu-open')); });
    el.querySelectorAll('[data-ap-menu-close]').forEach(function (c) {
      c.addEventListener('click', function () { setOpen(false); });
    });
    drawer.addEventListener('click', function (e) { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('menu-open')) setOpen(false);
    });
  }

  /* ------------------------------------------------------------ section tabs */
  /* Pins the tab bar under the header once the page scrolls past it, and
     highlights the tab whose section is under the reading line. A spacer
     holds the bar's height so nothing jumps when it leaves the flow. */
  function tabs() {
    var bar = document.querySelector('[data-ap-tabs]');
    if (!bar) return;
    var wrap = bar.parentElement;                       // stays in flow; marks the natural spot
    var spacer = document.querySelector('[data-ap-tabs-spacer]');
    var links = Array.prototype.slice.call(bar.querySelectorAll('[data-ap-tab]'));
    var targets = links.map(function (l) { return document.getElementById(l.dataset.apTab); }).filter(Boolean);
    var barH = bar.offsetHeight || 48;
    /* one ink bar that travels between tabs, rather than four that each grow
       from nothing — the difference between a widget and an instrument */
    var row = bar.querySelector('.ap-tabs-row');
    var ink = bar.querySelector('[data-ap-tabs-ink]');
    var moveInk = function () {
      if (!ink || !row) return;
      var on = links.filter(function (l) { return l.classList.contains('is-active'); })[0] || links[0];
      if (!on || !on.offsetWidth) return;
      row.classList.add('has-ink');
      ink.style.setProperty('--ap-ink-x', on.offsetLeft + 'px');
      ink.style.setProperty('--ap-ink-w', on.offsetWidth);
    };
    var headerH = function () {
      return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ap-header-h')) || 72;
    };

    // a plain fragment jump must land below the pinned bars too
    targets.forEach(function (t) { t.style.scrollMarginTop = 'calc(var(--ap-header-h, 72px) + ' + barH + 'px)'; });

    var stuck = false, ticking = false;
    var update = function () {
      ticking = false;
      var h = headerH();
      var shouldStick = wrap.getBoundingClientRect().top <= h;
      if (shouldStick !== stuck) {
        stuck = shouldStick;
        bar.classList.toggle('is-stuck', stuck);
        if (spacer) spacer.style.height = stuck ? barH + 'px' : '0px';
      }
      var line = h + barH + 40;
      /* the section nearest above the reading line wins, by position rather than
         tab order, because phones show the reels before Features */
      var active = targets[0] || null, activeTop = -Infinity;
      targets.forEach(function (t) {
        var top = t.getBoundingClientRect().top;
        if (top <= line && top > activeTop) { active = t; activeTop = top; }
      });
      links.forEach(function (l) {
        var on = !!active && l.dataset.apTab === active.id;
        l.classList.toggle('is-active', on);
        if (on) l.setAttribute('aria-current', 'true'); else l.removeAttribute('aria-current');
      });
      moveInk();
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', update);
    if (row) row.addEventListener('scroll', moveInk, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(moveInk).catch(function () {});
    update();

    // keep the active tab in view on phones when the bar itself scrolls
    links.forEach(function (l) {
      l.addEventListener('click', function () {
        links.forEach(function (x) { x.classList.toggle('is-active', x === l); });
        moveInk();
        l.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced ? 'auto' : 'smooth' });
      });
    });
  }

  /* ------------------------------------------------- showcase colour dots */
  /* Colourway switcher, and the slideshow that advances it on its own. The
     dots stay the control surface — auto-advance just moves the same index. */
  function showcase() {
    document.querySelectorAll('[data-ap-showcase]').forEach(function (root) {
      var section = root.closest('section') || root.parentElement;
      var slides = root.querySelectorAll('[data-ap-slide]');
      var dots = section.querySelectorAll('[data-ap-dot]');
      var label = section.querySelector('[data-ap-slide-label]');
      var detail = section.querySelector('[data-ap-slide-detail]');
      var stats = section.querySelectorAll('[data-ap-stat]');
      var inView = false;

      /* The figures are static. The pulse that used to run along them is on
         the motion/live-rail branch; nothing here lights them. */
      var idle = null;
      var energise = function () {};
      if (!dots.length) return;
      var current = 0;
      var restart;

      var select = function (i) {
        current = i;
        slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
        dots.forEach(function (d, n) {
          var on = n === i;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', String(on));
        });
        if (label) label.textContent = dots[i].getAttribute('aria-label') || '';
        if (detail) detail.textContent = dots[i].getAttribute('data-detail') || '';
        /* numbers pinned to a slide only show for that slide; unpinned show always */
        stats.forEach(function (st) {
          var owner = st.getAttribute('data-stat-slide');
          st.hidden = !!owner && parseInt(owner, 10) !== i + 1;
        });
        energise();
      };

      dots.forEach(function (d, i) {
        d.addEventListener('click', function () {
          select(i);
          if (restart) restart();      // a deliberate pick earns a full dwell
        });
      });

      /* One step in either direction, wrapping. Arrows and swipe both land
         here, and both count as a deliberate pick. */
      var step = function (delta) {
        select((current + delta + dots.length) % dots.length);
        if (restart) restart();
      };
      var prev = section.querySelector('[data-ap-showcase-prev]');
      var next = section.querySelector('[data-ap-showcase-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      /* Swipe. Touch events, not pointer events, so a mouse drag over the
         image is never mistaken for one — the arrows are the desktop control.
         The stage carries `touch-action: pan-y`, so a vertical drag still
         scrolls the page and only the horizontal axis is ours. */
      var sx = 0, sy = 0, swiping = false;
      root.addEventListener('touchstart', function (e) {
        swiping = e.touches.length === 1;
        if (!swiping) return;
        sx = e.touches[0].clientX;
        sy = e.touches[0].clientY;
      }, { passive: true });
      root.addEventListener('touchend', function (e) {
        if (!swiping) return;
        swiping = false;
        var t = e.changedTouches[0];
        var dx = t.clientX - sx, dy = t.clientY - sy;
        /* Ignore anything short, and anything that reads as a scroll: the
           gesture has to be clearly more sideways than it is vertical. */
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        step(dx < 0 ? 1 : -1);
      }, { passive: true });

      select(0);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && !inView) { inView = true; energise(); }
            else if (!e.isIntersecting) { inView = false; window.clearTimeout(idle); }
          });
        }, { threshold: 0.2 }).observe(section);
      } else { inView = true; energise(); }

      var ms = parseInt(root.dataset.apShowcaseAutoplay || '0', 10);
      var timer = null;
      var stop = function () { if (timer) { clearInterval(timer); timer = null; } };
      var go = function () {
        if (timer) return;
        timer = setInterval(function () { select((current + 1) % dots.length); }, ms);
      };
      restart = function () { if (timer) { stop(); go(); } };

      if (!ms || reduced || dots.length < 2) return;

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { e.isIntersecting ? go() : stop(); });
        }, { threshold: 0.25 }).observe(root);
      } else go();

      /* Pause under the pointer — but only on a device that really hovers. On
         a touch screen `pointerenter` fires on tap and the matching leave can
         never come, which would park the carousel for good. */
      root.addEventListener('pointerenter', function () { if (hoverable()) stop(); });
      root.addEventListener('pointerleave', function () { if (hoverable()) go(); });
      document.addEventListener('visibilitychange', function () {
        document.hidden ? stop() : go();
      });
    });
  }

  /* ------------------------------------------------------- lifestyle cards */
  /* A scroll-snapped row with arrows and dots. The row is a real scroller,
     so a swipe works with or without this. */
  function lifestyle() {
    document.querySelectorAll('[data-ap-life]').forEach(function (root) {
      var track = root.querySelector('[data-ap-life-track]');
      var cards = Array.prototype.slice.call(root.querySelectorAll('[data-ap-life-card]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-ap-life-dot]'));
      var prev = root.querySelector('[data-ap-life-prev]');
      var next = root.querySelector('[data-ap-life-next]');
      if (!track || !cards.length) return;

      var step = function () {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
        return cards[0].offsetWidth + gap;
      };
      var index = function () { return Math.round(track.scrollLeft / step()); };
      var go = function (i) {
        i = Math.max(0, Math.min(cards.length - 1, i));
        track.scrollTo({ left: i * step(), behavior: reduced ? 'auto' : 'smooth' });
      };
      var sync = function () {
        var i = index();
        dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      };
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      if (prev) prev.addEventListener('click', function () { go(index() - 1); });
      if (next) next.addEventListener('click', function () { go(index() + 1); });
      dots.forEach(function (d, n) { d.addEventListener('click', function () { go(n); }); });
      sync();
    });
  }

  /* ------------------------------------------------------- the video arc */
  /* A fanned wall of portrait clips, cloned from the reference: 200px cards,
     32px gaps, rotateY fanning across the row, the fan centred on the section.

     Phones slide the whole fan on a CSS loop (.ap-arc-animate -> apSlideGallery)
     and keep the fixed geometry in still(). Desktop runs the same fan as an
     endless conveyor instead: the cards travel left to right forever, and each
     card's rotateY, scale and stacking come from where it is on screen, so the
     edges stay fanned and the middle flat while the clips move through. The set
     is cloned until the loop is wider than the viewport plus a card either side,
     so a card only ever wraps while it is off screen. Reduced motion keeps the
     still fan everywhere. The responsive `left` (44vw vs 50%) lives in CSS. */
  function arc() {
    var desktop = window.matchMedia('(min-width: 769px)');   // the phone loop is max-width: 768px
    var SPEED = 36;                                           // px per second, left to right
    var GAP = 32;

    document.querySelectorAll('[data-ap-arc]').forEach(function (root) {
      var originals = Array.prototype.slice.call(root.querySelectorAll('[data-ap-arc-card]'));
      var n = originals.length;
      if (!n) return;

      var cards = originals;
      var geo = null, offset = 0, raf = null, last = 0, moving = false;
      var seen = !('IntersectionObserver' in window);

      /* the entrance lives on the inner wrapper, because the card's own transform
         is the geometry. Each starts pulled most of the way back to the centre
         line and slides out to its place, middle card first. */
      var entrance = function (card, x, order) {
        var inner = card.querySelector('.ap-arc-inner');
        if (!inner) return;
        inner.style.setProperty('--ap-fan', (-x * 0.72).toFixed(1) + 'px');
        inner.style.setProperty('--i', order);
      };

      /* the still fan: one fixed transform per card (phones, reduced motion).
         Measured, not assumed: cards are 200px clips or wider captioned slides. */
      var still = function () {
        var w = originals[0].offsetWidth || 200;
        // scale ramps from 1.0 at the centre out to 1.15 at the edges
        var scales = originals.map(function (_c, a) {
          return 1 + 0.3 * Math.abs((n > 1 ? a / (n - 1) : 0.5) - 0.5);
        });
        // each card's left edge = previous edge + previous card's width + the gap
        var edge = [0];
        for (var k = 1; k < n; k++) edge[k] = edge[k - 1] + w * scales[k - 1] + GAP;
        var mid = Math.floor(n / 2);
        var centreX = edge[mid] + w * scales[mid] / 2;   // the fan is centred here
        originals.forEach(function (card, a) {
          var r = n > 1 ? a / (n - 1) : 0.5;
          var tx = (edge[a] + w * scales[a] / 2) - centreX;
          entrance(card, tx, Math.abs(a - mid));
          /* the `- 50%` pulls each card back by half its own width, so `left:50%`
             puts the middle of the fan on the middle of the section */
          card.style.transform =
            'translateX(calc(' + tx + 'px - 50%)) translateY(-50%) rotateY(' + ((0.5 - r) * 80) + 'deg) scale(' + scales[a] + ')';
          card.style.zIndex = String(10 - Math.abs(a - mid));
        });
      };

      /* phones: the CSS loop sweeps the whole fan, from its first card at the
         left edge to its last card at the right edge and back. The distance is
         measured off the laid-out cards once their 300ms settle has finished,
         so it holds for any card count or screen width. */
      var sweepTimer = 0, sweepRun = 0;
      var sweep = function () {
        window.clearTimeout(sweepTimer);
        sweepTimer = window.setTimeout(function () {
          if (desktop.matches || reduced) return;
          var box = root.getBoundingClientRect();   // moves with the loop, and so do the cards
          if (!box.width) return;
          var minL = Infinity, maxR = -Infinity;
          originals.forEach(function (card) {
            var r = card.getBoundingClientRect();
            minL = Math.min(minL, r.left - box.left);
            maxR = Math.max(maxR, r.right - box.left);
          });
          var pad = 16;
          var from = pad - minL;                    // first card at the left edge
          var to = box.width - pad - maxR;          // last card at the right edge
          if (from <= to) { root.classList.remove('has-sweep'); return; }   // the fan already fits
          var secs = Math.min(60, Math.max(16, 2 * (from - to) / 65));    // ~65px/s on average
          var apply = function () {
            root.style.setProperty('--ap-arc-from', from.toFixed(0) + 'px');
            root.style.setProperty('--ap-arc-to', to.toFixed(0) + 'px');
          };
          apply();
          root.style.setProperty('--ap-arc-dur', secs.toFixed(1) + 's');
          root.classList.add('has-sweep');

          /* The fan sits in its own perspective, so the projected cards do not
             move exactly in step with the track (they landed ~39px off at 390px).
             Check the two end states on the page itself: seek the loop to each
             end, read where the outer cards landed, nudge, repeat once, restore.
             The loop's CSS animation only exists a frame or so after has-sweep
             lands (on first load getAnimations() came back empty here), so wait
             for it and for it to leave pending; the seeks then run in a single
             microtask, so nothing paints mid-check. */
          var host = root.parentElement;               // not transformed, so a fixed frame
          var run = ++sweepRun, tries = 0;
          var correct = function () {
            if (run !== sweepRun || !host || !root.classList.contains('has-sweep')) return;
            var anim = root.getAnimations && root.getAnimations().filter(function (x) { return x.animationName === 'apArcSweep'; })[0];
            if (!anim) { if (tries++ < 10) window.setTimeout(correct, 100); return; }
            (anim.ready || Promise.resolve()).then(function () {
              if (run !== sweepRun) return;            // a resize started a newer measurement
              var dur = anim.effect.getComputedTiming().duration, was = anim.currentTime;
              var outer = function (t) {
                anim.currentTime = t;
                var h = host.getBoundingClientRect(), l = Infinity, r = -Infinity;
                originals.forEach(function (card) {
                  var q = card.getBoundingClientRect();
                  l = Math.min(l, q.left - h.left);
                  r = Math.max(r, q.right - h.left);
                });
                return { l: l, r: r, w: h.width };
              };
              for (var k = 0; k < 2; k++) {
                var atStart = outer(0), atEnd = outer(dur / 2);
                from += pad - atStart.l;
                to += (atEnd.w - pad) - atEnd.r;
                apply();
              }
              if (was != null) anim.currentTime = was;
            });
          };
          correct();
        }, 400);
      };

      /* -------- desktop conveyor -------- */
      var unclone = function () {
        root.querySelectorAll('[data-ap-arc-clone]').forEach(function (c) { c.parentNode.removeChild(c); });
        cards = originals;
      };

      var clone = function (copies) {
        unclone();
        for (var c = 1; c < copies; c++) {
          originals.forEach(function (card) {
            var dup = card.cloneNode(true);
            Array.prototype.slice.call(dup.attributes).forEach(function (at) {
              if (at.name.indexOf('data-shopify') === 0) dup.removeAttribute(at.name);   // theme-editor hooks stay on the originals
            });
            dup.setAttribute('data-ap-arc-clone', '');
            dup.setAttribute('aria-hidden', 'true');
            dup.querySelectorAll('a, button, [tabindex]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
            root.appendChild(dup);
            if (watchVideo) dup.querySelectorAll('video[data-ap-autoplay]').forEach(watchVideo);
          });
        }
        cards = Array.prototype.slice.call(root.querySelectorAll('[data-ap-arc-card]'));
      };

      var xAt = function (j) {
        var u = j * geo.pitch - (n - 1) / 2 * geo.pitch + offset;
        return ((u + geo.span / 2) % geo.span + geo.span) % geo.span - geo.span / 2;
      };

      /* one card's place on the loop decides its fan: -1 at the original left
         edge, +1 at the right, clamped so the far edges never turn side-on */
      var place = function () {
        cards.forEach(function (card, j) {
          var x = xAt(j);
          var d = Math.max(-1.2, Math.min(1.2, x / geo.half));
          var ad = Math.abs(d);
          card.style.transform =
            'translateX(calc(' + x.toFixed(1) + 'px - 50%)) translateY(-50%) rotateY(' + (-d * 40).toFixed(2) + 'deg) scale(' + (1 + 0.15 * ad).toFixed(3) + ')';
          card.style.zIndex = String(100 - Math.round(ad * 20));
        });
      };

      var tick = function (now) {
        raf = null;
        if (!moving || !seen || document.hidden) return;
        if (last) offset = (offset + SPEED * Math.min(now - last, 50) / 1000) % geo.span;
        last = now;
        place();
        raf = window.requestAnimationFrame(tick);
      };
      var play = function () {
        if (raf || !moving || !seen || document.hidden) return;
        last = 0;
        raf = window.requestAnimationFrame(tick);
      };
      var stop = function () { if (raf) { window.cancelAnimationFrame(raf); raf = null; } };

      var mode = function () {
        stop();
        if (desktop.matches && !reduced) {
          root.classList.remove('has-sweep');
          var pitch = (originals[0].offsetWidth || 200) + GAP;
          var copies = Math.max(2, Math.ceil((window.innerWidth + pitch * 3) / (n * pitch)));
          var fresh = !moving || !geo || geo.copies !== copies;
          if (fresh) clone(copies);
          /* written every frame, and a wrap has to jump rather than glide back
             across the row, so the cards' own transform transition is off */
          cards.forEach(function (card) { card.style.transition = 'none'; });
          geo = { pitch: pitch, span: cards.length * pitch, half: Math.max(pitch, (n - 1) / 2 * pitch), copies: copies };
          if (fresh) {
            cards.forEach(function (card, j) {
              var x = xAt(j);
              entrance(card, x, Math.min(6, Math.round(Math.abs(x) / pitch)));
            });
          }
          moving = true;
          place();
          play();
        } else {
          moving = false;
          unclone();
          originals.forEach(function (card) { card.style.transition = ''; });
          still();
          sweep();
        }
      };

      mode();
      window.addEventListener('load', mode);
      window.addEventListener('resize', mode, { passive: true });   // re-clones only when the copy count changes
      if (desktop.addEventListener) desktop.addEventListener('change', mode);
      else desktop.addListener(mode);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { seen = e.isIntersecting; if (seen) play(); else stop(); });
        }, { rootMargin: '200px 0px' }).observe(root);
      }
      document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else play(); });

      once(root, function () { root.classList.add('is-in'); }, 0.15);

      /* the fan leans a couple of degrees toward the pointer. Desktop only:
         on a touch screen there is no pointer to lean towards, and on phones
         the track is already sliding on its own. */
      if (reduced || !hoverable()) return;
      var section = root.closest('section') || root.parentElement;
      if (!section) return;
      var frame = 0;
      section.addEventListener('pointermove', function (e) {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = 0;
          var box = section.getBoundingClientRect();
          if (!box.width || !box.height) return;
          var dx = (e.clientX - box.left) / box.width - 0.5;
          var dy = (e.clientY - box.top) / box.height - 0.5;
          root.classList.add('is-tracking');
          root.style.setProperty('--ap-tilt', (dx * 5).toFixed(2) + 'deg');
          root.style.setProperty('--ap-tilt-x', (-dy * 2.2).toFixed(2) + 'deg');
        });
      }, { passive: true });
      section.addEventListener('pointerleave', function () {
        root.classList.remove('is-tracking');
        root.style.setProperty('--ap-tilt', '0deg');
        root.style.setProperty('--ap-tilt-x', '0deg');
      });
    });
  }


  /* --------------------------------------------------- before / after pair */
  function beforeAfter() {
    document.querySelectorAll('[data-ap-ba]').forEach(function (card, i) {
      /* is-wiping lights the seam only while the clip edge is travelling, so
         the reveal reads as a scan rather than a cross-fade. */
      var wiping = null;
      var reveal = function (on) {
        card.classList.toggle('is-after', on);
        if (reduced) return;
        card.classList.add('is-wiping');
        window.clearTimeout(wiping);
        wiping = window.setTimeout(function () { card.classList.remove('is-wiping'); }, 900);
      };
      /* The two modes are mutually exclusive, and the test runs per event
         rather than once at bind time so a hybrid device behaves like whatever
         it currently is. The hint under the cards is switched by the same
         media query in CSS, so the word and the behaviour cannot drift. */
      var hovering = false;
      card.addEventListener('mouseenter', function () {
        if (!hoverable()) return;
        hovering = true;
        reveal(true);
      });
      card.addEventListener('mouseleave', function () {
        hovering = false;
        if (!hoverable()) return;
        reveal(false);
      });
      card.addEventListener('click', function () {
        if (hoverable()) return;                  // hover already owns the reveal
        reveal(!card.classList.contains('is-after'));
      });
      /* Keyboard works in either mode — it is the only way in without a
         pointer, so it is never the one that gets switched off. */
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); reveal(!card.classList.contains('is-after')); }
      });

      // Wipe once on the way past so the effect is seen before anyone touches
      // it, then close again — leaving it open would mean the first tap wipes
      // the wrong way and the "Tap to reveal" chip reads as a lie.
      if (reduced || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.disconnect();
          window.setTimeout(function () {
            reveal(true);
            window.setTimeout(function () { if (!hovering) reveal(false); }, 1500);
          }, 400 * i + 300);
        });
      }, { threshold: 0.4 });
      io.observe(card);
    });
  }

  /* --------------------------------------------------------- reels shuffle */
  function reels() {
    document.querySelectorAll('[data-ap-reels]').forEach(function (root) {
      var grid = root.querySelector('[data-ap-reels-grid]');
      var btn = root.querySelector('[data-ap-shuffle]');
      if (!grid || !btn) return;
      var icon = btn.querySelector('.ap-shuffle-icon');
      var busy = false;

      btn.addEventListener('click', function () {
        if (busy) return;
        busy = true;
        btn.disabled = true;
        if (icon) { icon.classList.remove('is-spinning'); void icon.offsetWidth; icon.classList.add('is-spinning'); }
        grid.classList.add('is-shuffling');

        window.setTimeout(function () {
          var items = Array.prototype.slice.call(grid.children);
          for (var i = items.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            grid.appendChild(items[j]);
            items.splice(j, 1);
          }
          grid.classList.remove('is-shuffling');
          btn.disabled = false;
          busy = false;
        }, reduced ? 0 : 400);
      });
    });
  }

  /* ----------------------------------------------------------- reel viewer */
  function reelViewer() {
    document.querySelectorAll('[data-ap-reels]').forEach(function (root) {
      var lb = root.querySelector('[data-ap-reel-lightbox]');
      var grid = root.querySelector('[data-ap-reels-grid]');
      if (!lb || !grid) return;

      var video = lb.querySelector('[data-ap-lb-video]');
      var counter = lb.querySelector('[data-ap-lb-count]');
      var handle = lb.querySelector('[data-ap-lb-handle]');
      var stage = lb.querySelector('[data-ap-lb-stage]');
      var closeBtn = lb.querySelector('[data-ap-lb-close]');
      var at = 0, restoreFocus = null, touchY = 0;

      var tiles = function () {
        return Array.prototype.slice.call(grid.querySelectorAll('[data-ap-reel-src]'));
      };

      var show = function (n) {
        var list = tiles();
        if (!list.length) return;
        at = (n % list.length + list.length) % list.length;
        var tile = list[at];
        video.src = tile.getAttribute('data-ap-reel-src') || '';
        video.load();
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
        if (counter) counter.textContent = (at + 1) + ' / ' + list.length;
        if (handle) {
          var href = tile.getAttribute('data-ap-reel-link');
          if (href) { handle.setAttribute('href', href); handle.removeAttribute('aria-disabled'); }
          else { handle.removeAttribute('href'); handle.setAttribute('aria-disabled', 'true'); }
        }
      };

      var step = function (d) { show(at + d); };

      var open = function (n) {
        restoreFocus = document.activeElement;
        lb.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        show(n);
        if (closeBtn) closeBtn.focus();
      };

      var close = function () {
        lb.classList.remove('is-open', 'is-loud');
        document.body.style.overflow = '';
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.muted = true;
        if (restoreFocus && restoreFocus.focus) restoreFocus.focus();
      };

      grid.addEventListener('click', function (e) {
        var tile = e.target.closest('[data-ap-reel-src]');
        if (!tile) return;
        e.preventDefault();
        open(tiles().indexOf(tile));
      });

      var watchAll = root.querySelector('[data-ap-reel-watch-all]');
      if (watchAll) watchAll.addEventListener('click', function () { open(0); });

      if (closeBtn) closeBtn.addEventListener('click', close);
      var prev = lb.querySelector('[data-ap-lb-prev]');
      var next = lb.querySelector('[data-ap-lb-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
      var mute = lb.querySelector('[data-ap-lb-mute]');
      var toggleSound = function () {
        video.muted = !video.muted;
        lb.classList.toggle('is-loud', !video.muted);
        if (mute) mute.setAttribute('aria-pressed', String(video.muted));
      };
      video.addEventListener('click', toggleSound);
      if (mute) mute.addEventListener('click', function (e) { e.stopPropagation(); toggleSound(); });

      document.addEventListener('keydown', function (e) {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
        else if (e.key === 'Tab') {
          var f = lb.querySelectorAll('button, a[href]');
          if (!f.length) return;
          var first = f[0], lastEl = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
          else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
        }
      });

      if (stage) {
        stage.addEventListener('touchstart', function (e) { touchY = e.touches[0].clientY; }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          var d = touchY - e.changedTouches[0].clientY;
          if (Math.abs(d) > 60) step(d > 0 ? 1 : -1);
        }, { passive: true });
      }
    });
  }

  /* ----------------------------------------------------- reels drift (phones) */
  function reelsDrift() {
    document.querySelectorAll('[data-ap-reels-viewport]').forEach(function (vp) {
      var phone = window.matchMedia('(max-width: 639px)');
      var speed = parseFloat(vp.dataset.apReelsSpeed || '42');
      var raf = null, dir = 1, last = 0, held = false, holdTimer = null, seen = true;

      var travel = function () { return vp.scrollWidth - vp.clientWidth; };
      var canRun = function () { return phone.matches && !reduced && speed > 0 && travel() > 4; };

      var step = function (now) {
        if (!canRun()) { raf = null; return; }
        var max = travel();
        if (!last) last = now;
        var dt = Math.min(now - last, 50) / 1000;
        last = now;
        var next = vp.scrollLeft + dir * speed * dt;
        if (next >= max) { next = max; dir = -1; }
        else if (next <= 0) { next = 0; dir = 1; }
        vp.scrollLeft = next;
        raf = window.requestAnimationFrame(step);
      };

      var play = function () {
        if (raf || held || !seen || !canRun()) return;
        last = 0;
        raf = window.requestAnimationFrame(step);
      };
      var stop = function () { if (raf) { window.cancelAnimationFrame(raf); raf = null; } };

      var hold = function () {
        held = true; stop();
        window.clearTimeout(holdTimer);
        holdTimer = window.setTimeout(function () {
          held = false;
          dir = vp.scrollLeft >= travel() - 1 ? -1 : 1;
          play();
        }, 2600);
      };
      vp.addEventListener('touchstart', hold, { passive: true });
      vp.addEventListener('pointerdown', hold);
      vp.addEventListener('wheel', hold, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { seen = en.isIntersecting; seen ? play() : stop(); });
        }, { threshold: 0.2 }).observe(vp);
      } else play();

      document.addEventListener('visibilitychange', function () {
        document.hidden ? stop() : play();
      });

      var onBreak = function () { stop(); vp.scrollLeft = 0; play(); };
      if (phone.addEventListener) phone.addEventListener('change', onBreak);
      else phone.addListener(onBreak);
      window.addEventListener('resize', function () { if (!raf) play(); }, { passive: true });
    });
  }

  /* ------------------------------------------ autoplay only what's visible */
  function lazyVideo() {
    var vids = Array.prototype.slice.call(document.querySelectorAll('video[data-ap-autoplay]'));
    if (!vids.length) return;

    var start = function (v) {
      if (v.dataset.src && !v.getAttribute('src')) v.src = v.dataset.src;
      v.play().catch(function () {});
    };
    var near = function (v) {
      var r = v.getBoundingClientRect();
      if (!r.width && !r.height) return false;
      return r.top < window.innerHeight + 300 && r.bottom > -300;
    };

    vids.forEach(function (v) { if (near(v)) start(v); });

    if (!('IntersectionObserver' in window)) {
      window.addEventListener('scroll', function () {
        vids.forEach(function (v) { if (near(v) && v.paused) start(v); });
      }, { passive: true });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) start(e.target);
        else if (!e.target.paused) e.target.pause();
      });
    }, { rootMargin: '300px' });
    vids.forEach(function (v) { io.observe(v); });
    watchVideo = function (v) { io.observe(v); };
  }
  /* videos added after lazyVideo() ran (the reels conveyor's copies) are watched through this */
  var watchVideo = null;

  /* --------------------------------------------------- reveal on scroll */
  /* Everything that enters the page enters through here, once, and never
     moves again. `data-ap-reveal-group` numbers its children into `--i` so
     the CSS can stagger a row along reading order without the markup having
     to count anything. */
  function reveal() {
    document.querySelectorAll('[data-ap-reveal-group]').forEach(function (group) {
      var sel = group.getAttribute('data-ap-reveal-group');
      var kids = sel ? group.querySelectorAll(sel) : group.children;
      Array.prototype.forEach.call(kids, function (kid, i) { kid.style.setProperty('--i', i); });
    });

    var els = Array.prototype.slice.call(
      document.querySelectorAll('[data-ap-reveal], [data-ap-words], .ap-rail')
    );
    els.forEach(function (el) { if (el.hasAttribute('data-ap-words')) splitWords(el); });
    if (!els.length) return;

    if (reduced || !IO) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ hero */
  /* The page's opening move: the words rise out of their masks, then one
     current runs through the accent phrase and leaves it lit. Nothing about
     the hero loops afterwards — a hero that keeps moving is a screensaver. */
  function hero() {
    var el = document.querySelector('[data-ap-hero]');
    if (!el) return;
    var h = el.querySelector('[data-ap-words]');
    var words = h ? splitWords(h) : 0;

    once(el, function () {
      el.classList.add('is-in');
      if (h) h.classList.add('is-in');
      if (reduced || !h) return;
      /* The strike goes on the spans that render the words, not on the <b>
         around them: background-clip:text does not reach glyphs inside a
         descendant inline-block, and a <b> full of .ap-w masks paints nothing
         at all — the accent phrase would simply vanish mid-entrance. */
      var lit = h.querySelectorAll(':is(b, strong, i, em) .ap-w-i');
      if (!lit.length) return;
      window.setTimeout(function () {
        lit.forEach(function (el, i) {
          el.style.setProperty('--ap-cur-i', i);
          el.classList.add('ap-current');
          fire(el);
        });
      }, 260 + words * 46 + 420);
    }, 0.02);
  }

  /* --------------------------------------------------------- FAQ accordion */
  /* <details> drops its content the moment `open` goes away, so there is no
     height to animate down from. Opening is driven by a class instead, and
     `open` is held on until the close has finished playing. The 0fr → 1fr
     grid row does the sizing, so there is no height maths anywhere. */
  function faqAccordion() {
    document.querySelectorAll('[data-ap-faq-item]').forEach(function (item) {
      var summary = item.querySelector('summary');
      var body = item.querySelector('[data-ap-faq-body]');
      if (!summary || !body) return;
      item.classList.add('is-armed');            // the CSS now defers to .is-open
      if (item.open) item.classList.add('is-open');

      var closing = null;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        window.clearTimeout(closing);
        if (reduced) {
          item.open = !item.open;
          item.classList.toggle('is-open', item.open);
          return;
        }
        if (item.open) {
          item.classList.remove('is-open');
          closing = window.setTimeout(function () { item.open = false; }, 430);
        } else {
          item.open = true;
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () { item.classList.add('is-open'); });
          });
        }
      });
    });
  }

  /* --------------------------------------------------------------- reviews */
  function reviews() {
    document.querySelectorAll('[data-ap-reviews-more]').forEach(function (btn) {
      var section = btn.closest('section') || document;
      var grid = section.querySelector('[data-ap-reviews]');
      if (!grid) return;
      btn.addEventListener('click', function () {
        grid.querySelectorAll('.ap-review.is-hidden').forEach(function (c, i) {
          if (i < 3) c.classList.remove('is-hidden');
        });
        if (!grid.querySelector('.ap-review.is-hidden')) btn.parentElement.remove();
      });
    });
  }

  /* ---------------------------------------------------- delivery estimate */
  /* A client-side estimate: dispatch days from the product plus a transit
     band by PIN prefix, skipping Sundays. It is labelled as an estimate. */
  function pincode() {
    document.querySelectorAll('[data-ap-pin-check]').forEach(function (btn) {
      var root = btn.closest('[data-ap-product]') || document;
      var input = root.querySelector('[data-ap-pin]');
      var out = root.querySelector('[data-ap-pin-out]');
      if (!input || !out) return;
      var run = function () {
        var pin = (input.value || '').trim();
        if (!/^[1-9][0-9]{5}$/.test(pin)) {
          out.textContent = 'Enter a valid 6-digit PIN code.';
          out.classList.add('text-accent');
          return;
        }
        out.classList.remove('text-accent');
        var dispatch = parseInt(root.getAttribute('data-ap-dispatch') || '2', 10) || 2;
        var metro = /^(11|12|40|41|56|60|70|50|38|30|20|22|39|42|45|48|52|53|58|64|69|75|80)/.test(pin);
        var days = dispatch + (metro ? 3 : 5);
        var d = new Date(), added = 0;
        while (added < days) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0) added++; }
        var when = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
        out.textContent = 'Estimated delivery by ' + when + ' to ' + pin + ' · free shipping';
      };
      btn.addEventListener('click', run);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); run(); } });
    });
  }

  /* --------------------------------------------------------- product page */
  /* Shared by the product template and the home-page buy box. */
  function productPage() {
    var root = document.querySelector('[data-ap-product]');
    if (!root) return;

    /* gallery ------------------------------------------------------------ */
    root.querySelectorAll('[data-ap-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.dataset.apThumb;
        root.querySelectorAll('.ap-pdp-main').forEach(function (img) {
          img.classList.toggle('is-active', img.dataset.apMainMedia === id);
        });
        root.querySelectorAll('[data-ap-thumb]').forEach(function (b) {
          var on = b === btn;
          /* .ap-pdp-thumb owns the selected border in the CSS — the JS only
             says which one is selected, it does not pick the colour */
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', String(on));
        });
      });
    });

    /* quantity ----------------------------------------------------------- */
    var qty = root.querySelector('[data-ap-qty-input]');
    root.querySelectorAll('[data-ap-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        var next = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.apQty, 10));
        qty.value = next;
      });
    });

    /* variants ----------------------------------------------------------- */
    var data = root.querySelector('[data-ap-variants]');
    var variants = [];
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }

    var idField = root.querySelector('[data-ap-variant-id]');
    var addBtn = root.querySelector('[data-ap-add]');
    var addText = root.querySelector('[data-ap-add-text]');
    var money = function (cents) {
      var whole = cents % 100 === 0;
      var n = (cents / 100).toFixed(whole ? 0 : 2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return 'Rs. ' + n;
    };

    var selected = function () {
      var out = [];
      root.querySelectorAll('[data-ap-option-index]').forEach(function (input) {
        if (input.checked) out[+input.dataset.apOptionIndex] = input.value;
      });
      return out;
    };

    var apply = function () {
      var opts = selected();
      var match = variants.find(function (v) {
        return opts.every(function (o, i) { return !o || v.options[i] === o; });
      });
      if (!match) return;

      if (idField) idField.value = match.id;
      if (root.hasAttribute('data-ap-sync-url')) {
        var url = new URL(window.location);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }

      var cur = root.querySelector('[data-ap-price-current]');
      if (cur) cur.textContent = money(match.price);
      var cmp = root.querySelector('[data-ap-price-compare]');
      var save = root.querySelector('[data-ap-price-save]');
      if (cmp) {
        var onSale = match.compare_at_price && match.compare_at_price > match.price;
        cmp.textContent = onSale ? money(match.compare_at_price) : '';
        cmp.hidden = !onSale;
        if (save) {
          save.hidden = !onSale;
          if (onSale) {
            /* floor, not round: Liquid's `| divided_by:` truncates, so rounding here made
               the badge jump 28% -> 29% on a variant switch that did not change the price.
               Truncation is also the safe direction — never claim a bigger discount than
               the arithmetic supports. */
            var pct = Math.floor((match.compare_at_price - match.price) / match.compare_at_price * 100);
            save.textContent = (save.dataset.apSaveFormat || 'Save {n}%').replace('{n}', pct);
          }
        }
      }
      root.querySelectorAll('[data-ap-option-label]').forEach(function (el, i) {
        if (opts[i]) el.textContent = opts[i];
      });
      if (addBtn) addBtn.disabled = !match.available;
      if (addText) addText.textContent = match.available ? (addText.dataset.label || 'Add to cart') : 'Sold out';
    };

    root.querySelectorAll('[data-ap-option-index]').forEach(function (input) {
      input.addEventListener('change', apply);
    });

    /* add to cart -------------------------------------------------------- */
    var form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      if (!window.fetch) return;
      e.preventDefault();
      var label = addText ? addText.textContent : '';
      if (addText) addText.textContent = 'Adding…';
      if (addBtn) addBtn.disabled = true;

      fetch('/cart/add.js', { method: 'POST', body: new FormData(form),
                              headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, b: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.b.description || res.b.message || 'Could not add to cart');
          if (addText) addText.textContent = 'Added ✓';
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (cart) document.dispatchEvent(new CustomEvent('ap:cart-updated', { detail: cart }));
          window.setTimeout(function () {
            if (addText) addText.textContent = label;
            if (addBtn) addBtn.disabled = false;
          }, 1400);
        })
        .catch(function (err) {
          if (addText) addText.textContent = err.message.slice(0, 40) || 'Try again';
          window.setTimeout(function () {
            if (addText) addText.textContent = label;
            if (addBtn) addBtn.disabled = false;
          }, 2200);
        });
    });
  }

  /* --------------------------------------------------------------- cart */
  function cartPage() {
    var root = document.querySelector('[data-ap-cart]');
    if (!root) return;
    root.querySelectorAll('[data-ap-line]').forEach(function (line) {
      var input = line.querySelector('[data-ap-line-input]');
      var idx = line.dataset.apLine;
      var change = function (q) {
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: +idx, quantity: q }),
        }).then(function (r) { return r.json(); })
          .then(function () { window.location.reload(); })
          .catch(function () { window.location.reload(); });
      };
      line.querySelectorAll('[data-ap-line-qty]').forEach(function (b) {
        b.addEventListener('click', function () {
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(b.dataset.apLineQty, 10));
          input.value = next;
          change(next);
        });
      });
      input.addEventListener('change', function () { change(Math.max(0, parseInt(input.value, 10) || 0)); });
    });
  }

  /* ------------------------------------------------------------ cart count */
  document.addEventListener('ap:cart-updated', function (e) {
    var n = e.detail && e.detail.item_count;
    document.querySelectorAll('[data-ap-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('hidden', !n);
    });
  });

  ready(function () {
    header(); marquee(); tabs(); faqTabs(); faqAccordion(); showcase(); lifestyle(); arc();
    beforeAfter(); reels(); reelViewer(); reelsDrift(); lazyVideo(); reveal(); hero();
    reviews(); pincode(); productPage(); cartPage();
  });
})();
